import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface QuickLogWidgetProps {
  childId: string;
  logType: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function QuickLogWidget({
  childId,
  logType,
  onClose,
  onSuccess,
}: QuickLogWidgetProps): React.ReactElement {
  const [notes, setNotes] = useState('');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high'>('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const logTypeLabels: Record<string, string> = {
    seizure: 'Seizure',
    medication: 'Medication',
    symptom: 'Symptom',
    sleep: 'Sleep',
    feeding: 'Feeding',
    therapy: 'Therapy',
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // API call would go here
      // await createHealthLog({ childId, logType, notes, severity });
      
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));
      
      onSuccess();
    } catch (error) {
      console.error('Failed to create log:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-display font-semibold text-navy">
            Quick Log: {logType ? logTypeLabels[logType] || logType : 'Entry'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Severity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Severity
            </label>
            <div className="flex gap-2">
              {(['low', 'medium', 'high'] as const).map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setSeverity(level)}
                  className={`
                    flex-1 py-2 px-3 rounded-lg border text-sm font-medium capitalize
                    transition-colors
                    ${severity === level
                      ? level === 'low'
                        ? 'bg-green-100 border-green-500 text-green-700'
                        : level === 'medium'
                        ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                        : 'bg-red-100 border-red-500 text-red-700'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage focus:border-transparent"
              placeholder="Add any additional details..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Saving...' : 'Save Entry'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default QuickLogWidget;
